import React, { useState, useEffect, useRef } from 'react';
import { View, Text, ScrollView, TouchableOpacity, RefreshControl, StyleSheet, Modal, TextInput } from 'react-native';
import CacheDataExplorer from './CacheDataExplorer';
import LoadingSpinner from './LoadingSpinner';

/**
 * Admin Cache Monitoring Tab
 * 
 * Features:
 * - Manual cache refresh trigger
 * - Real-time logs display
 * - Browse all cached fixtures
 * - Live countdown to next auto-refresh
 * - Cache health and statistics
 */
const CacheMonitoringTab = () => {
  const [cacheStats, setCacheStats] = useState(null);
  const [cacheLogs, setCacheLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [countdown, setCountdown] = useState(null);
  
  // Live monitor states
  const [showLiveMonitor, setShowLiveMonitor] = useState(false);
  const [liveLogStream, setLiveLogStream] = useState([]);
  const [refreshRunning, setRefreshRunning] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);
  const [liveFixtureCount, setLiveFixtureCount] = useState(0);
  const [recentFixtures, setRecentFixtures] = useState([]);
  const [showRefreshDropdown, setShowRefreshDropdown] = useState(false);
  
  // Ref for auto-scrolling terminal
  const scrollViewRef = useRef(null);
  
  const functionsUrl = __DEV__ 
    ? 'http://127.0.0.1:5001/bet-optimized-fe922/us-central1'
    : 'https://us-central1-bet-optimized-fe922.cloudfunctions.net';
  
  // Fetch cache stats from backend
  const fetchCacheStats = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${functionsUrl}/getCacheStats`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setCacheStats(data);
      } else {
        throw new Error(data.error || 'Failed to fetch cache stats');
      }
    } catch (err) {
      console.error('[CacheMonitoring] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // Fetch cache logs
  const fetchCacheLogs = async () => {
    try {
      const response = await fetch(`${functionsUrl}/getCacheLogs`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) throw new Error('Failed to fetch logs');
      
      const data = await response.json();
      if (data.success) {
        setCacheLogs(data.logs || []);
      }
    } catch (err) {
      console.error('[CacheLogs] Error:', err);
    }
  };
  
  // Manually trigger cache refresh
  const triggerCacheRefresh = async () => {
    try {
      setTriggering(true);
      
      const response = await fetch(`${functionsUrl}/triggerCacheRefresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[TriggerRefresh] HTTP Error:', response.status, errorText);
        throw new Error(`HTTP ${response.status}: ${errorText || 'Failed to trigger refresh'}`);
      }
      
      const data = await response.json();
      console.log('[TriggerRefresh] Response:', data);
      
      if (data.success && data.started) {
        // Open live monitor modal
        setLiveLogStream([
          { timestamp: new Date(), message: '🚀 Cache refresh started...', type: 'info' },
          { timestamp: new Date(), message: '⏱️ Estimated duration: 3-5 minutes', type: 'info' }
        ]);
        setRefreshRunning(true);
        setShowLiveMonitor(true);
        
        // Start polling for logs
        startLogPolling();
      } else if (data.running) {
        // Already running - show existing run in monitor
        alert(`⚠️ ${data.message}\n\nOpening monitor to show current progress...`);
        setRefreshRunning(true);
        setShowLiveMonitor(true);
        startLogPolling();
      } else {
        throw new Error(data.error || data.message || 'Unknown error');
      }
    } catch (err) {
      console.error('[TriggerRefresh] Error:', err);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };
  
  // Trigger SGP cache refresh only
  const triggerSGPRefresh = async () => {
    try {
      setTriggering(true);
      setShowRefreshDropdown(false);
      
      const response = await fetch(`${functionsUrl}/refreshAllSGPs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          limit: 10,
          options: {
            legCounts: [2, 3],
            topNPerLeg: 25,
            sportsbooks: ['DraftKings', 'BetMGM', 'Caesars'],
            minEV: 0
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to trigger SGP refresh`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setLiveLogStream([
          { timestamp: new Date(), message: '🎯 SGP cache refresh started...', type: 'info' },
          { timestamp: new Date(), message: '📊 Generating SGPs for up to 10 fixtures...', type: 'info' },
          { timestamp: new Date(), message: '⏱️ Estimated duration: 5-10 minutes', type: 'info' }
        ]);
        setRefreshRunning(true);
        setShowLiveMonitor(true);
        alert('SGP refresh started! Monitor progress in the live terminal.');
      } else {
        throw new Error(data.error || 'Failed to start SGP refresh');
      }
    } catch (err) {
      console.error('[TriggerSGPRefresh] Error:', err);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };
  
  // Trigger full refresh (odds + SGPs)
  const triggerFullRefresh = async () => {
    try {
      setTriggering(true);
      setShowRefreshDropdown(false);
      
      // Start with live monitor showing
      setLiveLogStream([
        { timestamp: new Date(), message: '🚀 Starting FULL refresh (Odds + SGPs)...', type: 'info' },
        { timestamp: new Date(), message: '📊 [1/2] Triggering odds cache refresh...', type: 'info' }
      ]);
      setRefreshRunning(true);
      setShowLiveMonitor(true);
      
      // First, trigger odds refresh
      const oddsResponse = await fetch(`${functionsUrl}/triggerCacheRefresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!oddsResponse.ok) {
        throw new Error('Failed to start odds refresh');
      }
      
      const oddsData = await oddsResponse.json();
      
      if (oddsData.success && oddsData.started) {
        setLiveLogStream(prev => [...prev, 
          { timestamp: new Date(), message: '✅ Odds refresh started!', type: 'info' },
          { timestamp: new Date(), message: '⏳ Waiting 5 seconds before starting SGPs...', type: 'info' }
        ]);
        
        // Wait 5 seconds
        await new Promise(resolve => setTimeout(resolve, 5000));
        
        // Then trigger SGP refresh
        setLiveLogStream(prev => [...prev,
          { timestamp: new Date(), message: '🎯 [2/2] Triggering SGP cache refresh...', type: 'info' }
        ]);
        
        const sgpResponse = await fetch(`${functionsUrl}/refreshAllSGPs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            limit: 10,
            options: {
              legCounts: [2, 3],
              topNPerLeg: 25,
              sportsbooks: ['DraftKings', 'BetMGM', 'Caesars'],
              minEV: 0
            }
          })
        });
        
        const sgpData = await sgpResponse.json();
        
        if (sgpData.success) {
          setLiveLogStream(prev => [...prev,
            { timestamp: new Date(), message: '✅ SGP refresh started!', type: 'info' },
            { timestamp: new Date(), message: '🎉 FULL refresh initiated! Both processes running in background.', type: 'info' },
            { timestamp: new Date(), message: '⏱️ Estimated total duration: 10-15 minutes', type: 'info' }
          ]);
          
          // Start polling for logs
          startLogPolling();
          
          // ⚠️ REMOVED: Alert is redundant since we show the live monitor
          // alert('Full refresh started! Both odds and SGPs are being generated. Monitor progress in the live terminal.');
        } else {
          setLiveLogStream(prev => [...prev,
            { timestamp: new Date(), message: `⚠️ SGP refresh failed: ${sgpData.error}`, type: 'error' }
          ]);
        }
      } else if (oddsData.running) {
        alert('Odds refresh is already running. Opening monitor...');
        startLogPolling();
      } else {
        throw new Error(oddsData.error || 'Failed to start odds refresh');
      }
    } catch (err) {
      console.error('[TriggerFullRefresh] Error:', err);
      setLiveLogStream(prev => [...prev,
        { timestamp: new Date(), message: `❌ Error: ${err.message}`, type: 'error' }
      ]);
      alert(`❌ Error: ${err.message}`);
    } finally {
      setTriggering(false);
    }
  };
  
  // Poll for new logs while refresh is running
  const startLogPolling = () => {
    const startTime = Date.now();
    let pollCount = 0;
    
    const pollInterval = setInterval(async () => {
      pollCount++;
      const elapsed = Math.floor((Date.now() - startTime) / 1000);
      
      console.log(`[LiveMonitor] Poll #${pollCount} at ${elapsed}s`);
      
      try {
        // Fetch latest logs
        const logsResponse = await fetch(`${functionsUrl}/getCacheLogs?limit=50`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (logsResponse.ok) {
          const logsData = await logsResponse.json();
          if (logsData.success && logsData.logs) {
            // Filter logs from last 10 minutes only (to hide old errors)
            const tenMinutesAgo = Date.now() - (10 * 60 * 1000);
            const recentLogs = logsData.logs.filter(log => {
              const logTime = log.timestamp?.toDate?.() || new Date(log.timestamp?._seconds * 1000 || 0);
              return logTime.getTime() > tenMinutesAgo;
            });
            
            // Convert to live log format with rich fixture details
            const formattedLogs = recentLogs.map(log => {
              let message = log.message;
              
              // ⭐ Enhanced display for phase logs (NEW!)
              if (log.type === 'phase_start') {
                message = `\n🔵 ${log.message}\n`;
              } else if (log.type === 'phase_complete') {
                message = `\n✅ ${log.message}\n`;
              }
              // ⭐ Enhanced display for fixture_processed logs
              else if (log.type === 'fixture_processed' && log.fixture) {
                const f = log.fixture;
                message = `🏀 ${f.away_team} @ ${f.home_team}\n`;
                if (log.stats) {
                  message += `   📊 ${log.stats.markets} markets, ${log.stats.odds?.toLocaleString() || '0'} odds\n`;
                }
                // Show sample odds
                if (log.sampleOdds && log.sampleOdds.length > 0) {
                  message += `   💰 Sample odds:\n`;
                  log.sampleOdds.slice(0, 2).forEach(sample => {
                    const oddsStr = sample.odds.map(o => 
                      `${o.selection || o.name}: ${o.price > 0 ? '+' : ''}${o.price}`
                    ).join(', ');
                    message += `      • ${sample.market}: ${oddsStr}\n`;
                  });
                }
              } else if (!message) {
                message = log.type === 'error' ? '❌ Error' : '✅ Success';
              }
              
              return {
                timestamp: log.timestamp,
                message,
                type: log.type,
                stats: log.stats,
                error: log.error,
                fixture: log.fixture,
                sampleOdds: log.sampleOdds
              };
            });
            
            // Always add a live status message if still running
            if (pollCount <= 100) { // Poll for up to 5 minutes (100 polls * 3s)
              formattedLogs.push({
                timestamp: new Date(),
                message: `⏳ Refresh in progress... (${elapsed}s elapsed)`,
                type: 'info'
              });
            }
            
            setLiveLogStream(formattedLogs);
            console.log(`[LiveMonitor] Loaded ${formattedLogs.length} log entries`);
          }
        }
        
        // Check refresh status
        const statsResponse = await fetch(`${functionsUrl}/getCacheStats`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setRefreshProgress(statsData);
          
          // Update live fixture count
          const fixtureCount = statsData.totals?.fixtures || 0;
          setLiveFixtureCount(fixtureCount);
          console.log(`[LiveMonitor] Fixture count: ${fixtureCount}, Running: ${statsData.refresh_running}`);
          
          // Only stop polling if:
          // 1. refresh_running is false AND
          // 2. We've polled at least 10 times (30 seconds) AND
          // 3. We have some fixtures cached
          if (!statsData.refresh_running && pollCount >= 10 && fixtureCount > 0) {
            console.log('[LiveMonitor] Stopping poll - refresh complete');
            setRefreshRunning(false);
            clearInterval(pollInterval);
            
            // ⭐ Only show completion banner if odds were actually cached
            const oddsStored = statsData.last_refresh?.stats?.oddsStored || 0;
            const fixturesWithOdds = statsData.last_refresh?.stats?.fixturesWithOdds || 0;
            
            if (oddsStored > 0) {
              // Success: Odds were cached
              setLiveLogStream(prev => [...prev, {
                timestamp: new Date(),
                message: '\n' +
                         '🎉 ========== REFRESH COMPLETE ==========\n\n' +
                         '📊 Final Stats:\n' +
                         `   🏢 Fixtures: ${fixtureCount}\n` +
                         `   ✅ With Odds: ${fixturesWithOdds}\n` +
                         `   ⏭️  Without Odds: ${statsData.last_refresh?.stats?.fixturesWithoutOdds || '?'}\n` +
                         `   📊 Markets: ${statsData.last_refresh?.stats?.marketsProcessed || '?'}\n` +
                         `   💎 Odds: ${oddsStored.toLocaleString()}\n` +
                         `   ⏱️  Duration: ${statsData.last_refresh?.duration_seconds?.toFixed(1) || 'N/A'}s\n\n` +
                         '✅ All data cached and ready!',
                type: 'success'
              }]);
            } else {
              // Info: No odds available yet
              setLiveLogStream(prev => [...prev, {
                timestamp: new Date(),
                message: '\n' +
                         '⏳ ========== REFRESH COMPLETE ==========\n\n' +
                         '📊 Summary:\n' +
                         `   🏢 Fixtures Found: ${fixtureCount}\n` +
                         `   ⏱️  Duration: ${statsData.last_refresh?.duration_seconds?.toFixed(1) || 'N/A'}s\n\n` +
                         '💡 No odds posted yet. Check back closer to game time!',
                type: 'info'
              }]);
            }
            
            // Refresh main stats
            await fetchCacheStats();
            await fetchCacheLogs();
          } else if (pollCount >= 100) {
            // Safety: stop after 5 minutes
            console.log('[LiveMonitor] Stopping poll - timeout');
            clearInterval(pollInterval);
            setRefreshRunning(false);
            
            // Add timeout message
            setLiveLogStream(prev => [...prev, {
              timestamp: new Date(),
              message: '\n⏱️  Polling timeout reached (5 minutes). Refresh may still be running in background.',
              type: 'info'
            }]);
          }
        }
        
        // Fetch recent fixtures (last 10)
        const fixturesResponse = await fetch(`${functionsUrl}/getAllCachedFixtures?limit=10`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });
        
        if (fixturesResponse.ok) {
          const fixturesData = await fixturesResponse.json();
          if (fixturesData.success && fixturesData.fixtures) {
            setRecentFixtures(fixturesData.fixtures);
            console.log(`[LiveMonitor] Recent fixtures: ${fixturesData.fixtures.length}`);
          }
        }
      } catch (err) {
        console.error('[LogPolling] Error:', err);
      }
    }, 3000); // Poll every 3 seconds
    
    // Store interval ID for cleanup
    window.cacheRefreshPollInterval = pollInterval;
    console.log('[LiveMonitor] Polling started - will poll for up to 5 minutes');
  };
  
  // Stop polling when monitor closes
  const closeLiveMonitor = () => {
    console.log('[LiveMonitor] Closing monitor and stopping polling');
    setShowLiveMonitor(false);
    if (window.cacheRefreshPollInterval) {
      clearInterval(window.cacheRefreshPollInterval);
      window.cacheRefreshPollInterval = null;
    }
  };
  
  // Clear stuck refresh status (for debugging)
  const clearStuckStatus = async () => {
    try {
      const confirm = window.confirm('Clear stuck refresh status? This will allow new refreshes to start.');
      if (!confirm) return;
      
      // Import Firebase
      const { getFirestore, doc, setDoc } = await import('firebase/firestore');
      const db = getFirestore();
      
      await setDoc(doc(db, '_cache_metadata', 'refresh_status'), {
        running: false,
        cleared_at: new Date(),
        cleared_manually: true
      });
      
      alert('✅ Stuck status cleared! You can now trigger a new refresh.');
      await fetchCacheStats();
    } catch (err) {
      console.error('[ClearStatus] Error:', err);
      alert(`❌ Error: ${err.message}`);
    }
  };
  
  // Calculate countdown to next refresh (hourly at :00)
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const nextHour = new Date(now);
      nextHour.setHours(now.getHours() + 1, 0, 0, 0);
      const diffMs = nextHour - now;
      const minutes = Math.floor(diffMs / 60000);
      const seconds = Math.floor((diffMs % 60000) / 1000);
      setCountdown(`${minutes}m ${seconds}s`);
    };
    
    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, []);
  
  // Load on mount and refresh every 30 seconds
  useEffect(() => {
    fetchCacheStats();
    fetchCacheLogs();
    
    const interval = setInterval(() => {
      fetchCacheStats();
      fetchCacheLogs();
    }, 30000); // Auto-refresh every 30s
    
    return () => clearInterval(interval);
  }, []);
  
  // Pull to refresh
  const onRefresh = () => {
    setRefreshing(true);
    fetchCacheStats();
    fetchCacheLogs();
  };
  
  // Get health color
  const getHealthColor = (health) => {
    switch (health) {
      case 'healthy': return '#10B981';
      case 'stale': return '#F59E0B';
      case 'critical': return '#EF4444';
      case 'never_refreshed': return '#6B7280';
      default: return '#6B7280';
    }
  };
  
  // Get health text
  const getHealthText = (health) => {
    switch (health) {
      case 'healthy': return '✅ Healthy';
      case 'stale': return '⚠️ Stale';
      case 'critical': return '🔴 Critical';
      case 'never_refreshed': return '⚪ Never Refreshed';
      default: return '❓ Unknown';
    }
  };
  
  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp._seconds * 1000);
    return date.toLocaleString();
  };
  
  // Render loading state
  if (loading && !cacheStats) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <LoadingSpinner size={32} color="#8B5CF6" />
          <Text style={styles.loadingText}>Loading cache stats...</Text>
        </View>
      </View>
    );
  }
  
  // Render error state
  if (error && !cacheStats) {
    return (
      <View style={styles.container}>
        <View style={styles.centerContent}>
          <Text style={styles.errorTitle}>❌ Error Loading Stats</Text>
          <Text style={styles.errorMessage}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchCacheStats}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }
  
  return (
    <ScrollView 
      style={styles.container}
      refreshControl={
        <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor="#8B5CF6" />
      }
    >
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.title}>🔄 Cache Monitoring</Text>
        <Text style={styles.subtitle}>Live backend cache statistics</Text>
      </View>
      
      {/* Info Card - Dual-Endpoint Strategy */}
      <View style={styles.infoCard}>
        <Text style={styles.infoTitle}>📚 How Caching Works</Text>
        <Text style={styles.infoText}>
          The system uses a <Text style={styles.infoBold}>dual-endpoint strategy</Text> to provide comprehensive coverage:
        </Text>
        <View style={styles.infoList}>
          <Text style={styles.infoListItem}>
            <Text style={styles.infoEmoji}>✅</Text> <Text style={styles.infoBold}>Fixtures WITH Odds</Text> - Games with live betting data (via /fixtures/active)
          </Text>
          <Text style={styles.infoListItem}>
            <Text style={styles.infoEmoji}>⏳</Text> <Text style={styles.infoBold}>Upcoming Fixtures</Text> - Future games for schedule display (via /fixtures)
          </Text>
        </View>
        <Text style={styles.infoFooter}>
          This ensures users always see relevant data - live odds when available, upcoming schedules when not.
        </Text>
      </View>
      
      {/* Quick Actions */}
      <View style={styles.actionsCard}>
        {/* Split Button: Main action + Dropdown */}
        <View style={styles.splitButtonWrapper}>
          <View style={styles.splitButtonContainer}>
            <TouchableOpacity 
              style={[styles.splitButtonMain, triggering && styles.actionButtonDisabled]} 
              onPress={triggerFullRefresh}
              disabled={triggering}
            >
              {triggering ? (
                <LoadingSpinner size={16} color="#FFFFFF" />
              ) : (
                <Text style={styles.actionButtonText}>🔄 Refresh Cache</Text>
              )}
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={[styles.splitButtonDropdown, triggering && styles.actionButtonDisabled]}
              onPress={() => setShowRefreshDropdown(!showRefreshDropdown)}
              disabled={triggering}
            >
              <Text style={styles.dropdownArrow}>{showRefreshDropdown ? '▲' : '▼'}</Text>
            </TouchableOpacity>
          </View>
          
          {/* Dropdown Menu */}
          {showRefreshDropdown && (
            <View style={styles.dropdownMenu}>
              <TouchableOpacity 
                style={styles.dropdownItem}
                onPress={triggerCacheRefresh}
              >
                <Text style={styles.dropdownItemText}>📊 Refresh Odds Cache Only</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.dropdownItem}
                onPress={triggerSGPRefresh}
              >
                <Text style={styles.dropdownItemText}>🎯 Refresh SGP Cache Only</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={[styles.dropdownItem, styles.dropdownItemHighlight]}
                onPress={triggerFullRefresh}
              >
                <Text style={[styles.dropdownItemText, styles.dropdownItemTextHighlight]}>🔄 Refresh All Caches</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>
        
        {cacheStats?.refresh_running && (
          <TouchableOpacity 
            style={[styles.actionButton, { backgroundColor: '#EF4444' }]} 
            onPress={clearStuckStatus}
          >
            <Text style={styles.actionButtonText}>🔧 Clear Stuck Status</Text>
          </TouchableOpacity>
        )}
      </View>
      
      {/* Countdown Timer */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>⏱️ Next Auto-Refresh</Text>
        <View style={styles.countdownContainer}>
          <Text style={styles.countdownText}>{countdown || 'Calculating...'}</Text>
          <Text style={styles.countdownSubtext}>Runs every hour at :00</Text>
        </View>
      </View>
      
      {/* Cache Health Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Cache Health</Text>
        <View style={[styles.healthBadge, { backgroundColor: getHealthColor(cacheStats?.cache_health) + '20' }]}>
          <Text style={[styles.healthText, { color: getHealthColor(cacheStats?.cache_health) }]}>
            {getHealthText(cacheStats?.cache_health)}
          </Text>
        </View>
      </View>
      
      {/* Last Refresh Card */}
      <View style={styles.card}>
        <Text style={styles.cardTitle}>Last Refresh</Text>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Time:</Text>
          <Text style={styles.statValue}>{formatTimestamp(cacheStats?.last_refresh?.timestamp)}</Text>
        </View>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Age:</Text>
          <Text style={styles.statValue}>
            {cacheStats?.last_refresh?.age_minutes !== null 
              ? `${cacheStats.last_refresh.age_minutes} minutes ago` 
              : 'Never'}
          </Text>
        </View>
        <View style={styles.statRow}>
          <Text style={styles.statLabel}>Duration:</Text>
          <Text style={styles.statValue}>
            {cacheStats?.last_refresh?.duration_seconds 
              ? `${cacheStats.last_refresh.duration_seconds.toFixed(2)}s` 
              : 'N/A'}
          </Text>
        </View>
      </View>
      
      {/* Refresh Stats Card */}
      {cacheStats?.last_refresh?.stats && (
        <View style={styles.card}>
          <Text style={styles.cardTitle}>📊 Last Refresh Details</Text>
          <Text style={styles.cardSubtitle}>4-phase caching process breakdown</Text>
          
          {/* Phase Breakdown */}
          <View style={styles.phaseContainer}>
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Phase 1A & 1B</Text>
              <Text style={styles.phaseDesc}>Fixture Discovery</Text>
              <Text style={styles.phaseValue}>{cacheStats.last_refresh.stats.fixturesProcessed} total fixtures</Text>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Phase 2</Text>
              <Text style={styles.phaseDesc}>Metadata Caching</Text>
              <View style={styles.phaseBreakdown}>
                <Text style={[styles.phaseSubValue, { color: '#10B981' }]}>
                  ✓ {cacheStats.last_refresh.stats.fixturesWithOdds || 0} with odds
                </Text>
                <Text style={[styles.phaseSubValue, { color: '#F59E0B' }]}>
                  ⏳ {cacheStats.last_refresh.stats.fixturesWithoutOdds || 0} upcoming
                </Text>
              </View>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Phase 3</Text>
              <Text style={styles.phaseDesc}>Odds Fetching</Text>
              <Text style={styles.phaseValue}>
                {cacheStats.last_refresh.stats.marketsProcessed} markets | {cacheStats.last_refresh.stats.oddsStored?.toLocaleString()} odds
              </Text>
            </View>
            
            <View style={styles.phaseItem}>
              <Text style={styles.phaseLabel}>Status</Text>
              <Text style={[styles.phaseValue, { 
                color: cacheStats.last_refresh.stats.errors > 0 ? '#EF4444' : '#10B981',
                fontWeight: 'bold' 
              }]}>
                {cacheStats.last_refresh.stats.errors > 0 
                  ? `❌ ${cacheStats.last_refresh.stats.errors} errors` 
                  : '✅ Success'}
              </Text>
            </View>
          </View>
        </View>
      )}
      
      {/* Cache Data Explorer - Browse all cached fixtures and odds */}
      <View style={styles.card}>
        <CacheDataExplorer functionsUrl={functionsUrl} />
      </View>
      
      {/* Live Refresh Monitor Modal */}
      <Modal
        visible={showLiveMonitor}
        animationType="slide"
        transparent={true}
        onRequestClose={closeLiveMonitor}
      >
        <View style={styles.modalOverlay}>
          <View style={[styles.modalContent, styles.liveMonitorContent]}>
            <View style={styles.modalHeader}>
              <View style={styles.liveMonitorHeaderLeft}>
                <Text style={styles.modalTitle}>🔴 Live Cache Refresh Monitor</Text>
                {refreshRunning && (
                  <LoadingSpinner size={16} color="#10B981" style={{ marginLeft: 12 }} />
                )}
              </View>
              <TouchableOpacity onPress={closeLiveMonitor}>
                <Text style={styles.modalClose}>✕</Text>
              </TouchableOpacity>
            </View>
            
            {/* Progress Summary */}
            {refreshProgress && (
              <View style={styles.liveProgressSummary}>
                <Text style={styles.liveProgressText}>
                  {refreshRunning 
                    ? '⏳ Cache refresh in progress...' 
                    : (refreshProgress.totals?.odds_estimated > 0 || cacheStats?.last_refresh?.stats?.oddsStored > 0)
                      ? '✅ Refresh complete!' 
                      : '⏳ Fixtures cached, odds not posted yet'}
                </Text>
                
                {refreshProgress.totals && (
                  <View style={styles.liveProgressBreakdown}>
                    <Text style={styles.liveProgressStats}>
                      📊 {refreshProgress.totals.fixtures || 0} total fixtures
                    </Text>
                    {cacheStats?.last_refresh?.stats && (
                      <View style={styles.liveProgressSplit}>
                        <Text style={[styles.liveProgressSubStat, { color: '#10B981' }]}>
                          ✓ {cacheStats.last_refresh.stats.fixturesWithOdds || 0} with odds
                        </Text>
                        <Text style={[styles.liveProgressSubStat, { color: '#F59E0B' }]}>
                          ⏳ {cacheStats.last_refresh.stats.fixturesWithoutOdds || 0} upcoming
                        </Text>
                      </View>
                    )}
                    <Text style={styles.liveProgressStats}>
                      📈 {refreshProgress.totals.markets || 0} markets | 
                      🎯 {refreshProgress.totals.odds_estimated?.toLocaleString() || 0} odds
                    </Text>
                  </View>
                )}
                
                {/* Live Fixture Counter */}
                {refreshRunning && liveFixtureCount > 0 && (
                  <View style={styles.liveCounterContainer}>
                    <Text style={styles.liveCounterText}>
                      🔥 {liveFixtureCount} fixtures processed
                    </Text>
                  </View>
                )}
              </View>
            )}
            
            {/* Recent Fixtures List (Live) */}
            {refreshRunning && recentFixtures.length > 0 && (
              <View style={styles.liveFixturesPanel}>
                <Text style={styles.liveFixturesTitle}>📋 Recently Cached Fixtures (Updates every 3s)</Text>
                <ScrollView style={styles.liveFixturesList} nestedScrollEnabled={true}>
                  {recentFixtures.map((fixture, idx) => (
                    <View key={fixture.id || idx} style={[
                      styles.liveFixtureItem,
                      !fixture.has_odds && styles.liveFixtureItemNoOdds
                    ]}>
                      <View style={styles.liveFixtureRow}>
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Text style={styles.liveFixtureLeague}>{fixture.league?.toUpperCase()}</Text>
                          {fixture.has_odds ? (
                            <Text style={styles.liveOddsStatus}>✓</Text>
                          ) : (
                            <Text style={styles.liveNoOddsStatus}>⏳</Text>
                          )}
                        </View>
                        <Text style={styles.liveFixtureTime}>
                          {new Date(fixture.last_updated?._seconds * 1000 || Date.now()).toLocaleTimeString()}
                        </Text>
                      </View>
                      <Text style={styles.liveFixtureMatchup}>
                        {fixture.home_team} vs {fixture.away_team}
                      </Text>
                      <Text style={styles.liveFixtureMarkets}>
                        {fixture.has_odds 
                          ? `📊 ${fixture.markets_count || 0} markets cached`
                          : '⏳ No odds available yet'
                        }
                      </Text>
                    </View>
                  ))}
                </ScrollView>
              </View>
            )}
            
            {/* Terminal-like Log Output */}
            <View style={styles.liveTerminal}>
              <View style={styles.terminalHeader}>
                <Text style={styles.terminalTitle}>CONSOLE OUTPUT</Text>
                <Text style={styles.terminalSubtitle}>Updates every 3 seconds</Text>
              </View>
              
              <ScrollView 
                style={styles.terminalBody}
                contentContainerStyle={styles.terminalBodyContent}
                ref={scrollViewRef}
                onContentSizeChange={() => scrollViewRef.current?.scrollToEnd({ animated: true })}
              >
                {liveLogStream.length === 0 ? (
                  <Text style={styles.terminalLine}>
                    <Text style={styles.terminalTimestamp}>{new Date().toLocaleTimeString()}</Text>
                    <Text style={styles.terminalMessage}> Waiting for logs...</Text>
                  </Text>
                ) : (
                  liveLogStream.map((log, idx) => (
                    <View key={idx} style={styles.terminalLogBlock}>
                      <Text style={styles.terminalLine}>
                        <Text style={styles.terminalTimestamp}>
                          {(() => {
                            try {
                              if (!log.timestamp) return '';
                              // Handle Firestore timestamp
                              if (log.timestamp.toDate) {
                                return log.timestamp.toDate().toLocaleTimeString();
                              }
                              // Handle _seconds format
                              if (log.timestamp._seconds) {
                                return new Date(log.timestamp._seconds * 1000).toLocaleTimeString();
                          }
                              // Handle regular Date or timestamp string
                              const date = new Date(log.timestamp);
                              return isNaN(date.getTime()) ? '' : date.toLocaleTimeString();
                            } catch (e) {
                              return '';
                            }
                          })()}
                        </Text>
                        <Text style={[
                          styles.terminalMessage,
                          log.type === 'error' && styles.terminalError,
                          log.type === 'success' && styles.terminalSuccess,
                          log.type === 'info' && styles.terminalInfo
                        ]}>
                          {' '}{log.message}
                        </Text>
                      </Text>
                      {log.stats && (
                        <Text style={styles.terminalStats}>
                          {'  '}├─ Fixtures: {log.stats.fixturesProcessed} | 
                          Markets: {log.stats.marketsProcessed} | 
                          Odds: {log.stats.oddsStored}
                        </Text>
                      )}
                      {log.error && (
                        <Text style={styles.terminalError}>
                          {'  '}└─ Error: {log.error}
                        </Text>
                      )}
                    </View>
                  ))
                )}
              </ScrollView>
            </View>
            
            {/* Action Buttons */}
            <View style={styles.liveMonitorActions}>
              {!refreshRunning && (
                <TouchableOpacity 
                  style={styles.liveActionButton}
                  onPress={closeLiveMonitor}
                >
                  <Text style={styles.liveActionButtonText}>✓ Close Monitor</Text>
                </TouchableOpacity>
              )}
              {refreshRunning && (
                <Text style={styles.liveMonitorNote}>
                  Monitor will auto-close when refresh completes, or click ✕ to close now
                </Text>
              )}
            </View>
          </View>
        </View>
      </Modal>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0F172A',
  },
  centerContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    color: '#94A3B8',
    fontSize: 16,
    marginTop: 16,
  },
  errorTitle: {
    color: '#EF4444',
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  errorMessage: {
    color: '#94A3B8',
    fontSize: 14,
    textAlign: 'center',
    marginBottom: 20,
  },
  retryButton: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  header: {
    padding: 20,
    paddingBottom: 12,
  },
  title: {
    color: '#FFFFFF',
    fontSize: 28,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  subtitle: {
    color: '#94A3B8',
    fontSize: 14,
  },
  
  // ⭐ Info Card Styles
  infoCard: {
    backgroundColor: '#1E293B',
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 16,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#8B5CF6',
  },
  infoTitle: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  infoText: {
    color: '#CBD5E1',
    fontSize: 14,
    lineHeight: 20,
  },
  infoBold: {
    fontWeight: '700',
    color: '#FFFFFF',
  },
  infoList: {
    marginTop: 12,
    marginBottom: 12,
  },
  infoListItem: {
    color: '#CBD5E1',
    fontSize: 13,
    lineHeight: 22,
    marginBottom: 6,
  },
  infoEmoji: {
    fontSize: 14,
  },
  infoFooter: {
    color: '#94A3B8',
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 8,
  },
  
  actionsCard: {
    marginHorizontal: 16,
    marginBottom: 16,
    gap: 12,
  },
  actionButton: {
    backgroundColor: '#8B5CF6',
    paddingVertical: 16,
    paddingHorizontal: 20,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 50,
  },
  actionButtonDisabled: {
    backgroundColor: '#64748B',
  },
  actionButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  countdownContainer: {
    alignItems: 'center',
    paddingVertical: 16,
  },
  countdownText: {
    color: '#8B5CF6',
    fontSize: 36,
    fontWeight: 'bold',
    fontVariant: ['tabular-nums'],
  },
  countdownSubtext: {
    color: '#94A3B8',
    fontSize: 14,
    marginTop: 4,
  },
  card: {
    backgroundColor: '#1E293B',
    marginHorizontal: 16,
    marginBottom: 16,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#334155',
  },
  cardTitle: {
    color: '#FFFFFF',
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 12,
  },
  healthBadge: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  healthText: {
    fontSize: 18,
    fontWeight: 'bold',
  },
  statRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  statLabel: {
    color: '#94A3B8',
    fontSize: 14,
  },
  statValue: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  footer: {
    padding: 20,
    alignItems: 'center',
  },
  footerText: {
    color: '#64748B',
    fontSize: 12,
    fontStyle: 'italic',
  },
  
  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: '#1E293B',
    width: '90%',
    maxHeight: '80%',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#334155',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  modalTitle: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: 'bold',
  },
  modalClose: {
    color: '#94A3B8',
    fontSize: 28,
    fontWeight: 'bold',
  },
  modalBody: {
    padding: 16,
  },
  
  // Log styles
  logEntry: {
    backgroundColor: '#0F172A',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: '#10B981',
  },
  logEntryError: {
    borderLeftColor: '#EF4444',
  },
  logTimestamp: {
    color: '#64748B',
    fontSize: 12,
    marginBottom: 4,
  },
  logType: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  logTypeSuccess: {
    color: '#10B981',
  },
  logTypeError: {
    color: '#EF4444',
  },
  logMessage: {
    color: '#FFFFFF',
    fontSize: 14,
    marginBottom: 4,
  },
  logStats: {
    color: '#94A3B8',
    fontSize: 12,
    marginTop: 4,
  },
  logStack: {
    color: '#EF4444',
    fontSize: 10,
    marginTop: 8,
    fontFamily: 'monospace',
  },
  noLogsText: {
    color: '#94A3B8',
    fontSize: 16,
    textAlign: 'center',
    paddingVertical: 40,
  },
  
  // Fixture browser styles
  filtersRow: {
    flexDirection: 'row',
    padding: 16,
    gap: 8,
    flexWrap: 'wrap',
  },
  filterButton: {
    backgroundColor: '#334155',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  filterButtonActive: {
    backgroundColor: '#8B5CF6',
  },
  filterButtonText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  fixtureCard: {
    backgroundColor: '#0F172A',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#334155',
  },
  fixtureHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  fixtureLeague: {
    color: '#8B5CF6',
    fontSize: 12,
    fontWeight: 'bold',
  },
  fixtureDate: {
    color: '#64748B',
    fontSize: 11,
  },
  fixtureMatchup: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  fixtureStatsRow: {
    flexDirection: 'row',
    gap: 16,
  },
  fixtureStatText: {
    color: '#94A3B8',
    fontSize: 12,
  },
  fixtureCardNoOdds: {
    borderColor: '#F59E0B',
    backgroundColor: '#1F2937',
    opacity: 0.8,
  },
  hasOddsBadge: {
    backgroundColor: '#10B98120',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#10B981',
  },
  hasOddsBadgeText: {
    color: '#10B981',
    fontSize: 10,
    fontWeight: 'bold',
  },
  noOddsBadge: {
    backgroundColor: '#F59E0B20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#F59E0B',
  },
  noOddsBadgeText: {
    color: '#F59E0B',
    fontSize: 10,
    fontWeight: 'bold',
  },
  fixtureNoOddsText: {
    color: '#F59E0B',
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 4,
  },
  liveFixtureItemNoOdds: {
    borderColor: '#F59E0B',
    backgroundColor: '#1F293B',
    opacity: 0.85,
  },
  liveOddsStatus: {
    color: '#10B981',
    fontSize: 14,
    fontWeight: 'bold',
  },
  liveNoOddsStatus: {
    color: '#F59E0B',
    fontSize: 14,
  },
  expandIcon: {
    color: '#8B5CF6',
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  noFixturesText: {
    color: '#94A3B8',
    fontSize: 16,
    textAlign: 'center',
    paddingVertical: 40,
  },
  
  // Expanded markets styles
  expandedSection: {
    marginTop: 16,
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#334155',
  },
  expandedHeader: {
    marginBottom: 12,
  },
  expandedTitle: {
    color: '#8B5CF6',
    fontSize: 14,
    fontWeight: 'bold',
  },
  marketSection: {
    marginBottom: 16,
    backgroundColor: '#1E293B',
    padding: 12,
    borderRadius: 6,
    borderLeftWidth: 3,
    borderLeftColor: '#8B5CF6',
  },
  marketHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  marketName: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
    flex: 1,
  },
  marketCount: {
    color: '#8B5CF6',
    fontSize: 11,
    fontWeight: 'bold',
  },
  oddRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  oddLeft: {
    flex: 1,
  },
  oddRight: {
    alignItems: 'flex-end',
    marginLeft: 12,
  },
  oddSelection: {
    color: '#F1F5F9',
    fontSize: 13,
    fontWeight: '500',
    marginBottom: 4,
  },
  oddSportsbook: {
    color: '#64748B',
    fontSize: 11,
  },
  oddPrice: {
    color: '#10B981',
    fontSize: 15,
    fontWeight: 'bold',
    marginBottom: 2,
  },
  oddEV: {
    fontSize: 10,
    fontWeight: '600',
  },
  oddEVPositive: {
    color: '#10B981',
  },
  oddEVNegative: {
    color: '#EF4444',
  },
  moreOddsText: {
    color: '#64748B',
    fontSize: 11,
    fontStyle: 'italic',
    textAlign: 'center',
    marginTop: 8,
  },
  
  // Live Monitor styles
  liveMonitorContent: {
    height: '90%',
    maxHeight: 800,
  },
  liveMonitorHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  liveProgressSummary: {
    backgroundColor: '#1E293B',
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#10B981',
  },
  liveProgressText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 8,
  },
  liveProgressStats: {
    color: '#94A3B8',
    fontSize: 14,
    marginBottom: 6,
  },
  liveProgressBreakdown: {
    marginTop: 8,
  },
  liveProgressSplit: {
    flexDirection: 'row',
    gap: 16,
    marginVertical: 8,
  },
  liveProgressSubStat: {
    fontSize: 13,
    fontWeight: '600',
  },
  liveTerminal: {
    flex: 1,
    backgroundColor: '#000000',
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#334155',
  },
  terminalHeader: {
    backgroundColor: '#1E293B',
    paddingVertical: 8,
    paddingHorizontal: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  terminalTitle: {
    color: '#10B981',
    fontSize: 12,
    fontWeight: 'bold',
    letterSpacing: 1,
  },
  terminalSubtitle: {
    color: '#64748B',
    fontSize: 10,
  },
  terminalBody: {
    flex: 1,
    padding: 12,
  },
  terminalBodyContent: {
    paddingBottom: 20,
  },
  terminalLogBlock: {
    marginBottom: 8,
  },
  terminalLine: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 2,
  },
  terminalTimestamp: {
    color: '#64748B',
    fontSize: 12,
    fontFamily: 'monospace',
    marginRight: 8,
  },
  terminalMessage: {
    color: '#FFFFFF',
    fontSize: 13,
    fontFamily: 'monospace',
    flex: 1,
  },
  terminalError: {
    color: '#EF4444',
  },
  terminalSuccess: {
    color: '#10B981',
  },
  terminalInfo: {
    color: '#3B82F6',
  },
  terminalStats: {
    color: '#8B5CF6',
    fontSize: 11,
    fontFamily: 'monospace',
    marginLeft: 80,
    marginTop: 2,
  },
  liveMonitorActions: {
    paddingTop: 16,
    alignItems: 'center',
  },
  liveActionButton: {
    backgroundColor: '#10B981',
    paddingHorizontal: 32,
    paddingVertical: 12,
    borderRadius: 8,
    minWidth: 200,
    alignItems: 'center',
  },
  liveActionButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  liveMonitorNote: {
    color: '#94A3B8',
    fontSize: 13,
    textAlign: 'center',
    fontStyle: 'italic',
  },
  
  // Live fixture tracking styles
  liveCounterContainer: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#334155',
  },
  liveCounterText: {
    color: '#10B981',
    fontSize: 16,
    fontWeight: 'bold',
    textAlign: 'center',
  },
  liveFixturesPanel: {
    backgroundColor: '#0F172A',
    borderRadius: 8,
    padding: 12,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#334155',
    maxHeight: 300,
  },
  liveFixturesTitle: {
    color: '#8B5CF6',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  liveFixturesList: {
    maxHeight: 250,
  },
  liveFixtureItem: {
    backgroundColor: '#1E293B',
    padding: 10,
    borderRadius: 6,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#10B981',
  },
  liveFixtureRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  liveFixtureLeague: {
    color: '#8B5CF6',
    fontSize: 11,
    fontWeight: 'bold',
  },
  liveFixtureTime: {
    color: '#64748B',
    fontSize: 10,
  },
  liveFixtureMatchup: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
    marginBottom: 4,
  },
  liveFixtureMarkets: {
    color: '#10B981',
    fontSize: 11,
  },
  
  // Split Button Styles
  splitButtonWrapper: {
    marginBottom: 12,
  },
  splitButtonContainer: {
    flexDirection: 'row',
  },
  splitButtonMain: {
    flex: 1,
    backgroundColor: '#8B5CF6',
    paddingVertical: 14,
    borderTopLeftRadius: 8,
    borderBottomLeftRadius: 8,
    alignItems: 'center',
  },
  splitButtonDropdown: {
    backgroundColor: '#7C3AED',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderTopRightRadius: 8,
    borderBottomRightRadius: 8,
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(255, 255, 255, 0.2)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  dropdownArrow: {
    color: '#FFFFFF',
    fontSize: 12,
    fontWeight: 'bold',
  },
  dropdownMenu: {
    backgroundColor: '#1E293B',
    borderRadius: 8,
    marginTop: 4,
    borderWidth: 1,
    borderColor: '#334155',
    overflow: 'hidden',
  },
  dropdownItem: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#334155',
  },
  dropdownItemHighlight: {
    backgroundColor: '#8B5CF620',
    borderBottomWidth: 0,
  },
  dropdownItemText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
  },
  dropdownItemTextHighlight: {
    color: '#8B5CF6',
    fontWeight: '700',
  },
  
  // Browse Odds Styles
  cardSubtitle: {
    color: '#64748B',
    fontSize: 13,
    marginBottom: 16,
    marginTop: 4,
  },
  browseOddsButton: {
    backgroundColor: '#8B5CF6',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 12,
  },
  browseOddsButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: 'bold',
  },
  browseOddsHint: {
    color: '#64748B',
    fontSize: 12,
    fontStyle: 'italic',
    textAlign: 'center',
  },
  
  // ⭐ Cache Breakdown Styles (Dual-Endpoint Strategy)
  cacheBreakdownContainer: {
    marginTop: 12,
  },
  cacheBreakdownSection: {
    marginBottom: 12,
  },
  cacheBreakdownLabel: {
    color: '#94A3B8',
    fontSize: 12,
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 8,
  },
  cacheBreakdownValue: {
    color: '#FFFFFF',
    fontSize: 36,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  cacheBreakdownSplit: {
    flexDirection: 'row',
    gap: 16,
  },
  cacheBreakdownItem: {
    flex: 1,
    backgroundColor: '#1E293B',
    padding: 12,
    borderRadius: 8,
    alignItems: 'center',
  },
  cacheBreakdownSubValue: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  cacheBreakdownSubLabel: {
    color: '#94A3B8',
    fontSize: 11,
    textAlign: 'center',
  },
  
  // ⭐ League Breakdown Styles
  leagueRow: {
    marginBottom: 12,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#1E293B',
  },
  leagueHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  leagueLabel: {
    color: '#E2E8F0',
    fontSize: 16,
    fontWeight: '600',
  },
  leagueCount: {
    color: '#8B5CF6',
    fontSize: 16,
    fontWeight: 'bold',
  },
  
  // ⭐ Phase Breakdown Styles
  phaseContainer: {
    marginTop: 16,
    gap: 16,
  },
  phaseItem: {
    backgroundColor: '#1E293B',
    padding: 14,
    borderRadius: 10,
    borderLeftWidth: 3,
    borderLeftColor: '#8B5CF6',
  },
  phaseLabel: {
    color: '#8B5CF6',
    fontSize: 12,
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 4,
  },
  phaseDesc: {
    color: '#94A3B8',
    fontSize: 12,
    marginBottom: 6,
  },
  phaseValue: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },
  phaseBreakdown: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 6,
  },
  phaseSubValue: {
    fontSize: 13,
    fontWeight: '600',
  },
  
  // ⭐ NEW: Sample Data Styles
  divider: {
    height: 1,
    backgroundColor: '#1E293B',
    marginVertical: 16,
  },
  sampleDataTitle: {
    color: '#E2E8F0',
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  marketTagsContainer: {
    flexDirection: 'row',
    marginBottom: 8,
    maxHeight: 40,
  },
  marketTag: {
    backgroundColor: '#1E293B',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#334155',
  },
  marketTagText: {
    color: '#94A3B8',
    fontSize: 12,
    fontWeight: '500',
  },
  sampleOddsContainer: {
    maxHeight: 400,
  },
  sampleOddsItem: {
    backgroundColor: '#1E293B',
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#334155',
  },
  sampleFixture: {
    color: '#E2E8F0',
    fontSize: 13,
    fontWeight: 'bold',
    marginBottom: 6,
  },
  sampleMarket: {
    color: '#8B5CF6',
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 8,
  },
  oddRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: '#0F172A',
    borderRadius: 4,
    marginBottom: 4,
  },
  oddSelection: {
    color: '#CBD5E1',
    fontSize: 12,
    flex: 2,
  },
  oddPrice: {
    color: '#10B981',
    fontSize: 12,
    fontWeight: 'bold',
    flex: 1,
    textAlign: 'center',
  },
  oddSportsbook: {
    color: '#64748B',
    fontSize: 11,
    flex: 1,
    textAlign: 'right',
  },
  oddEV: {
    color: '#F59E0B',
    fontSize: 10,
    fontWeight: '600',
    marginLeft: 8,
  },
});

export default CacheMonitoringTab;
